import { Context, EnrollmentStatus } from '@/pipeline/stateMachine';
import gql from 'graphql-tag';
import { AnyEventObject, StateConfig } from 'xstate';
import { enrollmentResultFields, GetEnrollment } from '../queries/westerosArcaneumQueries';

export interface UpsertEnrollmentMutationInput {
  id?: number;
  areDocumentsSent?: boolean;
  isContractAccepted?: boolean;
  enrollmentStatus?: EnrollmentStatus;
  partnerCode?: string | null;
  student?: {
    cpf?: string;
    rg?: string;
    rgIssuer?: string;
    rgIssueDate?: Date;
    rgIssueState?: string;
    nationality?: string;
    placeOfBirth?: string;
    birthDate?: Date;
    race?: string;
    phone?: string;
    email?: string;
    gender?: string;
    fullName?: string;
    schoolCategory?: string;
    schoolFormationYear?: number;
    schoolCity?: string;
    householdIncome?: string;
    motherName?: string;
    fatherName?: string;
    address?: {
      complement?: string;
      neighborhood?: string;
      state?: string;
      streetName?: string;
      streetNumber?: string;
      zipCode?: string;
      city?: string;
    };
    cityBirth?: string;
  };
  financialSponsor?: {
    cpf?: string;
    rg?: string;
    rgIssuer?: string;
    rgIssueDate?: Date;
    rgIssueState?: string;
    nationality?: string;
    placeOfBirth?: string;
    birthDate?: Date;
    race?: string;
    phone?: string;
    email?: string;
    gender?: string;
    fullName?: string;
    address?: {
      complement?: string;
      neighborhood?: string;
      state?: string;
      streetName?: string;
      streetNumber?: string;
      zipCode?: string;
      city?: string;
    };
  };
}

export interface UpsertEnrollment extends GetEnrollment {}

export interface ApplyVoucher {
  voucher: string;
}

export interface ApplyVoucherResult {
  applyVoucher: {
    success: boolean;
  };
}

export interface UpsertEnrollmentMutationResult {
  upsertEnrollment: UpsertEnrollment;
}

interface UpsertPipelineRun {
  id: number;
  state: StateConfig<Context, AnyEventObject>;
}

export interface UpsertPipelineRunMutationResult {
  upsertPipelineRun: UpsertPipelineRun;
}

export interface ReleaseAddonModuleToUserResult {
  releaseAddonModuleToUser: {
    success: boolean;
    message: string;
  };
}

export interface SubscribeInComplementaryModuleResult {
  subscribeInComplementaryModule: {
    success: boolean;
    message: object;
  };
}

export interface AddCreditCardPaymentMutationInput {
  flag?: string;
  number?: string;
  cvv?: string;
  month?: string;
  year?: string;
  ownerName?: string;
  ownerCPF?: string;
  isRecurrentCreditCard?: boolean;
}

interface AddCreditCardPayment extends GetEnrollment {}

export interface AddCreditCardPaymentMutationResult {
  addCreditCardPayment: AddCreditCardPayment;
}

export interface GenerateBankSlipMutationResult {
  generateBankSlip: string;
}

export interface GeneratePixDataMutationResult {
  generatePixData: {
    payload: string;
    qrcode: string;
    qrcode64: string;
    tagHtml: string;
  };
}

export interface GenerateContractMutationResult {
  generateContract: { backofficeContractHtml: string };
}

export interface TrackingIdentify {
  id?: string;
  name?: string;
  email?: string;
}

export interface TrackingEvent {
  eventName: string;
  eventData: { [key: string]: any };
  identify?: TrackingIdentify;
}

export interface TrackingEventInput {
  data: TrackingEvent;
}

export interface TrackEventResult {
  trackEvent: { success: boolean };
}

export interface RemoveRecurrentCreditCardResult {
  message: string;
}

export const upsertEnrollment = gql`
  mutation upsertEnrollment($data: UpsertEnrollmentMutationInput!) {
    upsertEnrollment(data: $data) {
      ${enrollmentResultFields}
    }
  }
`;

export const upsertPipelineRun = gql`
  mutation upsertPipelineRun($enrollmentId: Float!, $state: JSON!) {
    upsertPipelineRun(enrollmentId: $enrollmentId, state: $state) {
      id
      state
    }
  }
`;

export const addCreditCardPayment = gql`
  mutation addCreditCardPayment($data: AddCreditCardPaymentMutationInput!) {
    addCreditCardPayment(data: $data) {
      ${enrollmentResultFields}
    }
  }
`;

export const generateBankSlip = gql`
  mutation generateBankSlip {
    generateBankSlip
  }
`;

export const generatePixData = gql`
  mutation generatePixData {
    generatePixData {
      payload
      qrcode
      qrcode64
      tagHtml
    }
  }
`;

export const applyVoucher = gql`
  mutation applyVoucher($voucher: String!) {
    applyVoucher(voucher: $voucher) {
      success
    }
  }
`;

export const generateContract = gql`
  mutation generateContract {
    generateContract {
      backofficeContractHtml
    }
  }
`;

export const trackEvent = gql`
  mutation trackEvent($data: TrackingEventInput!) {
    trackEvent(data: $data) {
      success
    }
  }
`;

export const releaseAddonModuleToUser = gql`
  mutation releaseAddonModuleToUser(
    $tokenD: String!
    $course: String!
    $backofficeStudentAcademicRecord: String!
  ) {
    releaseAddonModuleToUser(
      tokenD: $tokenD
      course: $course
      backofficeStudentAcademicRecord: $backofficeStudentAcademicRecord
    ) {
      success
      message
    }
  }
`;

export const subscribeInComplementaryModule = gql`
  mutation SubscribeInComplementary(
    $descomplicaCourseId: String!
    $descomplicaCurriculumId: String!
    $academicRecordId: String
    $cpf: String
  ) {
    subscribeInComplementaryModule(
      descomplicaCourseId: $descomplicaCourseId
      descomplicaCurriculumId: $descomplicaCurriculumId
      academicRecordId: $academicRecordId
      cpf: $cpf
    ) {
      success
      message
    }
  }
`;

export const removeRecurrentCreditCard = gql`
  mutation removeRecurrentCreditCard {
    removeRecurrentCreditCard {
      message
    }
  }
`;
